import {
  BRANCHES,
  branchElement,
  branchesClash,
  branchesCombine,
  pillarAt,
  relationOf,
  STEMS,
  stemElement,
  stemsClash,
  stemsCombine,
  type Relation,
} from './ganzhi'

/**
 * 해·달·10년 흐름을 규칙으로 계산한다. 서버·클라이언트 공용 순수 함수.
 *
 * AI 는 여기서 나온 표를 근거로 "몇 월 무렵" 을 쓴다. 표에 없는 시기를 지어내지 않게 하려는 장치다.
 * 기본 문장도 같은 표에서 좋은 달·쉬어 갈 달을 뽑으므로 AI 와 폴백의 시기가 어긋나지 않는다.
 */

export const RELATIONS: readonly Relation[] = ['same', 'output', 'wealth', 'officer', 'resource']

/** 연도의 간지. 1984 = 甲子. 입춘 전후 구분 없이 그 해 전체를 대표하는 값으로 쓴다. */
export function yearGanZhi(year: number): string {
  if (!Number.isInteger(year)) throw new Error(`연도가 아니다: ${year}`)
  return pillarAt((((year - 1984) % 60) + 60) % 60)
}

export interface MonthFlow {
  /** 0 = 寅월(2월 초 시작) … 11 = 丑월(다음 해 1월 초 시작) */
  index: number
  ganZhi: string
  /** 이 달이 시작되는 양력 달 (1~12). 절기 기준이라 "그 달 초순부터 다음 달 초순까지" 다. */
  startMonth: number
  score: number
  relation: Relation
}

/**
 * 한 해의 월 간지 12개. 월지는 寅부터, 월간은 연간으로 정한다 (甲己년 → 丙寅월, 乙庚 → 戊寅 …).
 * 월 경계는 절기라 날짜는 해마다 하루 이틀 흔들리지만, "몇 월 무렵" 으로만 쓰므로 양력 달로 충분하다.
 */
export function monthPillarsOfYear(year: number): { ganZhi: string; startMonth: number }[] {
  const yearStem = STEMS.indexOf(yearGanZhi(year)[0]!)
  const firstStem = (yearStem * 2 + 2) % 10
  return Array.from({ length: 12 }, (_, i) => ({
    ganZhi: STEMS[(firstStem + i) % 10]! + BRANCHES[(2 + i) % 12]!,
    startMonth: ((i + 1) % 12) + 1,
  }))
}

/** 오늘의 운세와 같은 뼈대로 기간 하나의 흐름 점수(1~5)를 매긴다. 일지와의 합·충을 더 본다. */
export function periodScore(dayPillar: string, ganZhi: string): { score: number; relation: Relation } {
  const [dayStem, dayBranch] = [...dayPillar] as [string, string]
  const [stem, branch] = [...ganZhi] as [string, string]
  const me = stemElement(dayStem)
  const relation = relationOf(me, stemElement(stem))
  let score = { resource: 4, output: 4, same: 3, wealth: 3, officer: 2 }[relation]

  const branchRelation = relationOf(me, branchElement(branch))
  if (branchRelation === 'resource' || branchRelation === 'same') score += 1
  if (branchRelation === 'officer') score -= 1
  if (stemsCombine(dayStem, stem)) score += 1
  if (stemsClash(dayStem, stem)) score -= 1
  if (branchesCombine(dayBranch, branch)) score += 1
  if (branchesClash(dayBranch, branch)) score -= 1

  return { score: Math.max(1, Math.min(5, score)), relation }
}

export interface YearFlow {
  year: number
  ganZhi: string
  score: number
  relation: Relation
  months: MonthFlow[]
  /** 흐름이 좋은 달 3개의 시작 양력 달 (점수 높은 순, 같으면 이른 순) */
  bestMonths: number[]
  /** 상대적으로 숨 고르기 좋은 달 2개 (점수 낮은 순, 같으면 이른 순) */
  restMonths: number[]
}

export function yearFlow(dayPillar: string, year: number): YearFlow {
  const ganZhi = yearGanZhi(year)
  const { score, relation } = periodScore(dayPillar, ganZhi)
  const months: MonthFlow[] = monthPillarsOfYear(year).map((m, index) => ({
    index,
    ...m,
    ...periodScore(dayPillar, m.ganZhi),
  }))
  const byBest = [...months].sort((a, b) => b.score - a.score || a.index - b.index)
  const byRest = [...months].sort((a, b) => a.score - b.score || a.index - b.index)
  return {
    year,
    ganZhi,
    score,
    relation,
    months,
    bestMonths: byBest.slice(0, 3).map((m) => m.startMonth),
    restMonths: byRest.slice(0, 2).map((m) => m.startMonth),
  }
}

/** 지금 해에 걸린 대운. 첫 대운 전이면 null. */
export function currentDaeun<T extends { startYear: number }>(entries: readonly T[], year: number): T | null {
  let found: T | null = null
  for (const entry of entries) {
    if (entry.startYear <= year) found = entry
  }
  return found
}

